import { Outlet, useNavigate } from 'react-router-dom';
import { useActiveUser } from '../hooks/useActiveUser';
import LoadingSpinner from './LoadingSpinner';
import Button from './Button';

/**
 * Gate for routes that require a real Cognito user (Friends, Leaderboard).
 *
 * Anonymous users get an interstitial CTA instead of a redirect — keeping the
 * tab structure intact so they can navigate back to other parts of the app.
 */
export default function PrivateRoute() {
  const navigate = useNavigate();
  const { isLoading, isAuthed } = useActiveUser();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-night-bg">
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  if (isAuthed) return <Outlet />;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-night-bg px-4 pt-16 flex flex-col items-center text-center">
      <div className="w-14 h-14 bg-sky-500/15 rounded-2xl flex items-center justify-center mb-5">
        <svg
          className="w-7 h-7 text-sky-500"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M22 11v6" />
          <path d="M19 14h6" />
        </svg>
      </div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-night-text mb-1">
        Sign in to continue
      </h2>
      <p className="text-sm text-gray-600 dark:text-night-muted mb-6 max-w-xs">
        This area is for signed-in users — your sessions stay on this device until you sign in.
      </p>
      <div className="w-full max-w-xs space-y-2">
        <Button onClick={() => navigate('/login')}>Sign in</Button>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="w-full py-3 text-sm font-medium text-sky-600 dark:text-sky-400"
        >
          Keep browsing without an account
        </button>
      </div>
    </div>
  );
}
